import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Paper from "@mui/material/Paper";
import InputValidatorField from "../../../common/components/Inputs/InputValidatorField";
import { IsValidName, IsValidDescription, IsValidTypicalCost } from "../../../../utils/Validators";
import ButtonPrimary from "../../../common/components/Buttons/ButtonPrimary";
import TableWithActions from "../../../common/components/Wrappers/TableWithActions";
import { ThemeContext } from "styled-components";
import GroupSelector from "../../../common/components/GroupSelector";

const TemplateInfoStep = ({
    formData,
    onChange,
    onSubmit,
    disabled = false,
    isLoading = false,
    isCompleted = false
}) => {
    const handleSubmit = (e) => {
        e.preventDefault();
        if (!disabled && !isLoading) {
            onSubmit();
        }
    };

    const theme = useContext(ThemeContext);
    const navigate = useNavigate();
    const isValid = !!(formData.name && formData.description && formData.typical_cost && Number(formData.typical_cost) > 0);

    if (isCompleted) {
        return (
            <Box>
                {/*<WithActions forceSpacing showSuccessButton={isCompleted}>*/}
                {/*    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>*/}
                {/*        <Typography variant="h6" component="div" sx={{ mb: 2, textAlign: 'center' }}>*/}
                {/*            Configured*/}
                {/*        </Typography>*/}
                {/*    </Box>*/}
                {/*</WithActions>*/}
            </Box>
        )
    }

    return (
        <Box
            sx={{
                opacity: disabled ? 0.6 : 1,
                pointerEvents: disabled ? 'none' : 'auto',
                borderRadius: '8px',
                margin: 0,
                transition: 'all 0.3s ease',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
                paddingBottom: 0,
                overflow: 'hidden',
            }}
        >
            {/* Scrollable middle - same flex setup as Split and Charge steps */}
            <Box
                sx={{
                    flex: '1 1 0%',
                    minHeight: 0,
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', flexShrink: 0, pb: 2 }}>
                    <TableWithActions
                        showLeftAction="buffer"
                        showRightAction={true}
                        actionColumnWidth="56px"
                        forceSpacing={true}
                        containerProps={{
                            component: Paper,
                            sx: {
                                mb: 0,
                                borderRadius: 2,
                                backgroundColor: theme.colors.tableBackground || theme.colors.background,
                                boxShadow: theme.shadows?.table || '0 4px 12px rgba(0, 0, 0, 0.15)',
                                overflowX: 'hidden',
                                '& .MuiTable-root': {
                                    '& .MuiTableBody-root': {
                                        '& .MuiTableRow-root:last-child .MuiTableCell-root': {
                                            borderBottom: 'none',
                                        },
                                    },
                                },
                                '& .MuiTableCell-root:nth-of-type(2)': {
                                    paddingLeft: 0,
                                    paddingRight: 0,
                                    width: '100%',
                                    maxWidth: '100%',
                                    minWidth: 0,
                                    boxSizing: 'border-box',
                                },
                                '& .MuiTable-root': {
                                    width: '100%',
                                },
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }} />
                                <TableCell sx={{ color: theme.colors.brandSecondary, fontWeight: 700, fontSize: '1.75rem', pl: 0, py: 2 }}>
                                    <Typography sx={{ color: theme.colors.brandSecondary, fontWeight: 700, fontSize: '1.75rem' }}>Configuration</Typography>
                                </TableCell>
                                <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0, borderBottom: 'none' }} />
                                <TableCell sx={{ verticalAlign: 'top', borderBottom: 'none', minWidth: 0 }}>
                                    <Box
                                        component="form"
                                        id="setup-form"
                                        onSubmit={handleSubmit}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            width: '100%',
                                            maxWidth: '100%',
                                            minWidth: 0,
                                        }}
                                    >
                                        <InputValidatorField
                                            helperText="(Required, max 50 characters)"
                                            label="Name"
                                            name="name"
                                            value={formData.name || ''}
                                            handleChange={onChange}
                                            isValidInput={IsValidName}
                                            requirementsText="Required - provide a name to help identify this bill (max 50 characters)"
                                            disabled={disabled}
                                            InputLabelProps={{
                                                style: { color: theme.colors.borderContrast },
                                            }}
                                            InputProps={{
                                                style: { color: theme.colors.borderContrast },
                                            }}
                                            fullWidth
                                            sx={{ width: '100%', maxWidth: '100%', margin: 0, mb: 2 }}
                                        />
                                        <InputValidatorField
                                            helperText="(Required, max 255 characters)"
                                            label="Description"
                                            name="description"
                                            value={formData.description || ''}
                                            handleChange={onChange}
                                            isValidInput={IsValidDescription}
                                            requirementsText="Required - provide a description to help identify this bill (max 255 characters)"
                                            disabled={disabled}
                                            InputLabelProps={{
                                                style: { color: theme.colors.borderContrast },
                                            }}
                                            InputProps={{
                                                style: { color: theme.colors.borderContrast },
                                            }}
                                            fullWidth
                                            sx={{ width: '100%', maxWidth: '100%', margin: 0, mb: 2 }}
                                        />
                                        <InputValidatorField
                                            helperText="(Required, max 999,999,999)"
                                            label="Typical Cost"
                                            name="typical_cost"
                                            type="number"
                                            value={formData.typical_cost || ''}
                                            handleChange={onChange}
                                            isValidInput={IsValidTypicalCost}
                                            requirementsText="Required - help us estimate your costs (must be a positive integer, max 999,999,999)"
                                            disabled={disabled}
                                            InputLabelProps={{
                                                style: { color: theme.colors.borderContrast },
                                            }}
                                            InputProps={{
                                                style: { color: theme.colors.borderContrast },
                                            }}
                                            fullWidth
                                            sx={{ width: '100%', maxWidth: '100%', margin: 0, mb: 2 }}
                                        />
                                        <Box sx={{ mt: 1, width: '100%', minWidth: 0 }}>
                                            <GroupSelector
                                                value={formData.group_id ? String(formData.group_id) : ''}
                                                onChange={onChange}
                                                disabled={disabled || isLoading}
                                                showCreateOption={true}
                                                filterOwnedOnly={true}
                                                skipWrapper={true}
                                                sx={{ width: '100%' }}
                                            />
                                        </Box>
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0, borderBottom: 'none' }} />
                            </TableRow>
                        </TableBody>
                    </TableWithActions>
                </Box>
            </Box>

            {/* Footer - Cancel/Continue hybrid, same flex layout as Split and Charge steps */}
            {!disabled && (
                <Box
                    sx={{
                        flexShrink: 0,
                        width: '100%',
                        backgroundColor: theme.colors.backgroundPrimary,
                        paddingTop: 2,
                        paddingBottom: 0,
                        borderTop: `1px solid ${(theme.colors && theme.colors.backgroundPrimary) || 'transparent'}`,
                    }}
                >
                    <ButtonPrimary
                        type={isValid ? 'submit' : 'button'}
                        form={isValid ? 'setup-form' : undefined}
                        disabled={disabled || isLoading}
                        fullWidth
                        onClick={!isValid ? (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            navigate(-1);
                        } : undefined}
                    >
                        {isLoading ? 'Creating...' : (isValid ? 'Continue' : 'Cancel')}
                    </ButtonPrimary>
                </Box>
            )}
        </Box>
    );
};

export default TemplateInfoStep;
